import React, { useState, useRef, useEffect } from "react";
import {
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

const Container = styled("div")({
  display: "flex",
  flexDirection: "row",
  backgroundColor: "transparent",
});

const StepperContainer = styled("div")({
  width: "30%",
  padding: "16px",
  height: "100%",
  display: "flex",
  flexDirection: "row",
});

const TabsContainer = styled("div")({
  flex: 1,
  display: "flex",
  flexDirection: "column",
});

const IconsContainer = styled("div")({
  width: "32px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const ContentContainer = styled("div")({
  width: "70%",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "flex-start",
});

const ContentBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  padding: theme.spacing(4),
  borderRadius: "8px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
}));

const CustomTabLabel = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  textAlign: "right",
});

const HelperText = styled(Typography)({
  fontStyle: "italic",
  fontSize: "0.8rem",
  textAlign: "right",
  color: "#666",
});

const CustomStepIcon = styled("div")(({ theme, active, completed }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  borderRadius: "50%",
  backgroundColor: active || completed ? "#E84610" : "#e0e0e0",
  color: active || completed ? "#fff" : "#000",
  "& svg": {
    fontSize: 16,
  },
}));


const CvBuiderMenu = ({steps}) => {
  const [activeStep, setActiveStep] = useState(0);
  const tabRefs = useRef([]);
  const stepLabelRefs = useRef([]);


  const handleTabChange = (event, newValue) => {
    setActiveStep(newValue);
  };

  useEffect(() => {
    tabRefs.current.forEach((tab, index) => {
      if (tab && stepLabelRefs.current[index]) {
        const { height } = tab.getBoundingClientRect();
        stepLabelRefs.current[index].style.height = `${height}px`;
      }
    });
  }, [activeStep]); // Re-run on activeStep change to handle any dynamic height changes

  return (
    <Container>
      <StepperContainer>
        <TabsContainer>
          <Tabs
            disableRipple={true}
            orientation="vertical"
            value={activeStep}
            onChange={handleTabChange}
            sx={{
              "& .MuiTabs-flexContainer": {
                height: "100%",
                alignItems: "flex-end",
                justifyContent: "flex-start",
                marginRight:"32px"
              },
              "& .MuiTab-root": {
                alignItems: "flex-end",
                textAlign: "right",
                padding: "4px 8px",
                minHeight: "auto",
                textTransform: "none",
                width: "100%",
                marginBottom: "36px",
                "&.Mui-selected": {
                  backgroundColor: "transparent",
                },
                "&.Mui-focusVisible": {
                  outline: "none",
                  boxShadow: "none",
                },
                "&:focus": {
                  outline: "none",
                },
                "&:active": {
                  backgroundColor: "transparent",
                },
                "&:hover": {
                  backgroundColor: "transparent",
                },
              },
              "& .MuiTab-root:last-child": {
                marginBottom: 0,
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
              "& .MuiTouchRipple-root": {
                display: "none",
              },
            }}
          >
            {steps.map((step, index) => (
              <Tab
                key={step.label}
                disableRipple
                label={
                  <CustomTabLabel>
                    <Typography
                      sx={{
                        fontFamily: "Readex Pro",
                        fontSize: "20px",
                        textAlign: "right",
                        fontStyle: "normal",
                        fontWeight: 500,
                        lineHeight: "30px",
                      }}
                    >
                      {step.label}
                    </Typography>
                    <HelperText
                      sx={{
                        fontFamily: "Inter",
                        fontSize: "16px",
                        fontStyle: "normal",
                        fontWeight: 300,
                        lineHeight: "24px",
                      }}
                    >
                      {step.helperText}
                    </HelperText>
                  </CustomTabLabel>
                }
                ref={(el) => (tabRefs.current[index] = el)}
              />
            ))}
          </Tabs>
        </TabsContainer>
        <IconsContainer>
          <Stepper
            activeStep={activeStep}
            orientation="vertical"
            sx={{
              "& .MuiStep-root": {
                padding: "0",
                margin: "0",
              },
              "& .MuiStepLabel-root": {
                padding: "0",
                marginBottom: "0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiStepLabel-iconContainer": {
                padding: "0",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              },
              "& .MuiStepConnector-root": {
                width: "24px",
                minHeight: "36px",
                "& .MuiStepConnector-line": {
                  borderWidth: "1px",
                  borderColor: "#e0e0e0",
                  height: "36px",
                },
              },
              "& .MuiStepIcon-text": {
                display: "none",
              },
            }}
          >
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  ref={(el) => (stepLabelRefs.current[index] = el)}
                  icon={
                    <CustomStepIcon active={activeStep >= index}>
                      {step.icon}
                    </CustomStepIcon>
                  }
                />
              </Step>
            ))}
          </Stepper>
        </IconsContainer>
      </StepperContainer>
      <ContentContainer>
        <ContentBox>
          <Typography variant="h5" gutterBottom>
            {steps[activeStep].content}
          </Typography>
        </ContentBox>
      </ContentContainer>
    </Container>
  );
};

export default CvBuiderMenu;